import React, { useState, useEffect } from 'react';
import { Select, Spin, Empty, Tooltip } from 'antd';
import { InfoCircleOutlined } from '@ant-design/icons';
import useSonarPermissionGroups from '../../hooks/query/useSonarPermissionGroups';
import ParamFormItem from './ParamFormItem';
import { Param } from '../../cliDataSchema';

type PermissionGroup = {
  name: string;
  namespace: string;
  description?: string;
};

type OptionType = {
  label: string;
  value: string;
  description: string;
};

type Props = {
  selectedValue?: string;
  handleSelectChange: (value: string) => void;
  param: Param;
  visibleParams: Param[];
};

const SonarAndSynopsysSelection: React.FC<Props> = ({
  selectedValue,
  handleSelectChange,
  param,
  visibleParams,
}) => {
  const [disabled, setDisabled] = useState(false);
  const [filteredOptions, setFilteredOptions] = useState<OptionType[]>([]);
  const [visibleTooltip, setVisibleTooltip] = useState<string | null>(null);
  const { data: permissionGroups, isLoading } = useSonarPermissionGroups();

  useEffect(() => {
    if (permissionGroups) {
      const allOptions = permissionGroups.map((group: PermissionGroup) => ({
        label: `${group.name} (${group.namespace})`,
        value: group.namespace,
        description: group.description || 'No description available',
      }));
      setFilteredOptions(allOptions);
    }
  }, [permissionGroups]);

  const handleSearch = (value: string) => {
    if (!permissionGroups) {
      return;
    }

    const filtered = permissionGroups
      .map((group: PermissionGroup) => ({
        label: `${group.name} (${group.namespace})`,
        value: group.namespace,
        description: group.description || 'No description available',
      }))
      .filter((option) => {
        const combinedText = `${option.label} ${option.value}`.toLowerCase();
        return combinedText.includes(value.toLowerCase());
      });

    setFilteredOptions(filtered);
  };

  const handleDropdownVisibleChange = (open: boolean) => {
    if (open && permissionGroups) {
      const allOptions = permissionGroups.map((group: PermissionGroup) => ({
        label: `${group.name} (${group.namespace})`,
        value: group.namespace,
        description: group.description || 'No description available',
      }));
      setFilteredOptions(allOptions);
    }
  };

  const handleTooltipClick = (e: React.MouseEvent, value: string) => {
    e.stopPropagation();
    setVisibleTooltip((prev) => (prev === value ? null : value));
  };

  const renderDropdown = (menu: React.ReactNode) => <>{menu}</>;

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Backspace') {
      handleSelectChange('');
    }
  };

  return (
    <ParamFormItem
      param={param}
      disabled={disabled}
      setDisabled={setDisabled}
      visibleParams={visibleParams}
    >
      <Select
        value={selectedValue}
        disabled={disabled}
        allowClear
        placeholder="Select a group"
        onChange={handleSelectChange}
        onSearch={handleSearch}
        onKeyDown={handleKeyDown}
        onDropdownVisibleChange={handleDropdownVisibleChange}
        showSearch
        filterOption={false}
        dropdownRender={renderDropdown}
        notFoundContent={
          isLoading ? (
            <Spin size="small" />
          ) : (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description="No groups found"
            />
          )
        }
        style={{ width: '100%' }}
        options={filteredOptions.map((option) => ({
          label: (
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <span>{option.label}</span>
              <Tooltip
                title={option.description}
                visible={visibleTooltip === option.value}
                onVisibleChange={(visible) =>
                  !visible && setVisibleTooltip(null)
                }
              >
                <InfoCircleOutlined
                  style={{ marginLeft: 8, cursor: 'pointer' }}
                  onClick={(e) => handleTooltipClick(e, option.value)}
                />
              </Tooltip>
            </div>
          ),
          value: option.value,
        }))}
      />
    </ParamFormItem>
  );
};

export default SonarAndSynopsysSelection;
